import { FunctionComponent, memo, useState } from "react";
import { useField } from "formik";
import { Button } from "@edgetier/components";
import { faUndoAlt, faTimes, faCheck } from "@fortawesome/free-solid-svg-icons";
import MetricFrequencyChart from "../metric-frequency-chart";
import MetricSlider from "../metric-slider";
import MetricsDistributionsForm from "./metrics-distributions-form";
import { IProps } from "./metrics-distributions.types";
import { getMetricDistributionStartAndEndValue } from "utilities-for/metrics/get-metric-distributions-start-and-end-value";
import "./metrics-distributions.scss";

/**
 * Display a chart of interactions available a selected time or score range
 * selected by a slider
 * @param props.close      Callback to close the opened tooltip
 * @param props.metricData Metric distribution data
 * @param props.metricType Metric selected
 */
const MetricsDistributions: FunctionComponent<IProps> = ({
    maximumFieldName,
    metricData,
    metricType,
    minimumFieldName,
    close,
}) => {
    // Metric field data and helpers
    const [maximumField, , maximumFieldHelpers] = useField<number>(maximumFieldName);
    const [minimumField, , minimumFieldHelpers] = useField<number>(minimumFieldName);

    const { startValue, endValue } = getMetricDistributionStartAndEndValue(metricData);
    const inputDomain: [number, number] = [startValue, endValue];

    // Input values from metric distribution form
    const [inputValues, setInputValues] = useState<[number, number]>(
        typeof minimumField.value === "number" && typeof maximumField.value === "number"
            ? [minimumField.value, maximumField.value]
            : inputDomain
    );

    /**
     * Update highlight from input change
     * @param values
     */
    const handleInputChange = (values: [number, number]) => {
        setInputValues(values);
    };

    /**
     * Reset filter
     */
    const resetValues = () => {
        setInputValues([inputDomain[0], inputDomain[1]]);
    };

    /**
     * Submit values
     */
    const submitValues = () => {
        minimumFieldHelpers.setValue(inputValues[0]);
        maximumFieldHelpers.setValue(inputValues[1]);
        close();
    };

    return (
        <>
            <div className="interaction-metrics-distribution__chart">
                <MetricFrequencyChart metricData={metricData} />
                <MetricSlider
                    metricData={metricData}
                    metricType={metricType}
                    highlightRange={[
                        Math.max(inputDomain[0], inputValues[0]),
                        Math.min(inputDomain[1], inputValues[1]),
                    ]}
                    domain={inputDomain}
                    onChange={handleInputChange}
                />
                <MetricsDistributionsForm
                    metricType={metricType}
                    inputRange={inputValues}
                    onChange={handleInputChange}
                />
            </div>

            <div className="interaction-metrics-distribution__buttons">
                <Button
                    className="button--reset"
                    icon={faUndoAlt}
                    disabled={inputDomain[0] === inputValues[0] && inputDomain[1] === inputValues[1]}
                    styleName="negative"
                    type="button"
                    onClick={resetValues}
                >
                    Reset
                </Button>

                <Button className="button--cancel" icon={faTimes} styleName="neutral" type="button" onClick={close}>
                    Cancel
                </Button>

                <Button
                    className="button--submit"
                    icon={faCheck}
                    disabled={!Array.isArray(metricData) || metricData.length === 0}
                    styleName="positive"
                    type="button"
                    onClick={submitValues}
                >
                    Apply
                </Button>
            </div>
        </>
    );
};

export default memo(MetricsDistributions);
